import get from 'lodash.get';
import { format } from 'date-fns';
import {
    VirtualizedTableItem,
    VirtualizedTableColumns
} from 'services/virtualizedTable/types';

import { selectEntities } from 'services/entities/selectors';
import {
    selectFertilizerSchemaEntityTypeName,
    selectFertilizerSchemaInnerEntityTypePluralName
} from 'services/schemas/selectors';

import { selectPeopleKeyedById } from 'services/people/selectors';
import { Person } from 'services/people/types';
// @ts-ignore
import { selectActiveTableCellRowIds } from 'services/selectedTableCells/selectors';
import { FertilizerEventUuid } from 'services/fertilizerEvents/types';

import { makeSelectTableColumnWidths } from 'services/columnWidths/selectors';
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';
import formatDropdownOptions from 'utils/format-dropdown-options';
import {
    selectFirstDate,
    selectLastDate
    // @ts-ignore
} from 'components/DatePagination/selectors';
import {
    FIELD_TYPE_STRING,
    FIELD_TYPE_DROPDOWN,
    FIELD_TYPE_NUMBER,
    FIELD_TYPE_ENTITY_LIST,
    FIELD_TYPE_CONFIRMATION_CHECKBOX
} from 'components/VirtualizedTable/constants';
import {
    selectIsAdmin,
    selectIsManager,
    selectIsAssistantManager
} from 'services/usersMe/selectors';
import {
    selectFertilizerEvents,
    selectFilteredFertilizerEventsArray
} from 'services/fertilizerEvents/selectors';
import { FERTILIZER_EVENT_APPLICATION_METHOD_OPTIONS } from '../FertilizerEventFinalForm/components/FertilizerEventForm/components/FertilizerEventApplicationPage/components/FertilizerEventApplicationMethodDropdown/constants';

const selectFertilizerEventItems = createDeepEqualSelector(
    [selectFilteredFertilizerEventsArray, selectEntities],
    (fertilizerEventsArrayState, entities) =>
        fertilizerEventsArrayState.map(
            (fertilizerEvent): VirtualizedTableItem => {
                const {
                    entityId,
                    startDate,
                    fertilizerEvent: { operatorPersonId }
                } = fertilizerEvent;

                const entityPathNames = get(entities, [entityId, 'pathNames']);

                return {
                    ...fertilizerEvent,
                    key: `${fertilizerEvent.uuid}`,
                    startDateFormatted: format(new Date(startDate), 'MMM d'),
                    entityPathNames,
                    operatorPersonId
                };
            }
        )
);

const makeSelectFertilizerEventItems = (): typeof selectFertilizerEventItems =>
    selectFertilizerEventItems;

const selectPeopleOptions = createDeepEqualSelector(
    selectPeopleKeyedById,
    (users: Record<Person['id'], Person>) =>
        formatDropdownOptions(users, {
            keyField: 'id',
            textField: 'fullName'
        })
);

const selectFertilizerEventsTableColumns = createDeepEqualSelector(
    [
        makeSelectTableColumnWidths(),
        selectFertilizerSchemaEntityTypeName,
        selectFertilizerSchemaInnerEntityTypePluralName,
        selectPeopleOptions
    ],
    (
        tableColumnWidths,
        fertilizerSchemaEntityTypeName,
        fertilizerSchemaInnerEntityTypePluralName,
        personOptions
    ): VirtualizedTableColumns => {
        const result: VirtualizedTableColumns = [
            {
                label: 'Date',
                value: 'startDateFormatted',
                width: 100,
                type: FIELD_TYPE_STRING,
                validators: 'isRequired'
            },
            {
                label: fertilizerSchemaEntityTypeName || 'Area',
                value: 'entityPathNames',
                width: 250,
                type: FIELD_TYPE_STRING,
                validators: 'isRequired'
            },
            {
                label: 'Applicator',
                value: 'fertilizerEvent.operatorPersonId',
                width: 150,
                type: FIELD_TYPE_DROPDOWN,
                options: personOptions,
                validators: 'isRequired'
            },
            {
                label: 'Total Area (Ha)',
                value: 'fertilizerEvent.sumTotalArea',
                width: 200,
                type: FIELD_TYPE_NUMBER,
                validators: 'isRequired'
            },
            {
                label: 'App. Method',
                value: 'fertilizerEvent.applicationMethod',
                width: 150,
                type: FIELD_TYPE_DROPDOWN,
                validators: 'isRequired',
                options: FERTILIZER_EVENT_APPLICATION_METHOD_OPTIONS
            },
            {
                label: 'Tech. responsible person',
                value: 'fertilizerEvent.technicalPersonName',
                width: 150,
                type: FIELD_TYPE_STRING
            },
            {
                label: 'Justification',
                value: 'fertilizerEvent.justification',
                width: 150,
                type: FIELD_TYPE_STRING
            },
            {
                label: fertilizerSchemaInnerEntityTypePluralName || 'Applied',
                value: 'fertilizerEvent.fertilizerEntities',
                width: 150,
                type: FIELD_TYPE_ENTITY_LIST,
                disableSorting: true
            },
            {
                label: 'Completed',
                value: 'jobs[0].completedAt',
                width: 100,
                type: FIELD_TYPE_CONFIRMATION_CHECKBOX
            }
        ];

        return result.map((column) => {
            const { width, value } = column;
            return {
                ...column,
                width: get(tableColumnWidths, value, width)
            };
        });
    }
);

const selectFertilizerEventsTableIsSelectable = createDeepEqualSelector(
    selectIsAdmin,
    selectIsManager,
    selectIsAssistantManager,
    (isAdmin, isManager, isAssistantManager): boolean =>
        Boolean(isAdmin || isManager || isAssistantManager)
);

const makeSelectFertilizerEventsTableIsSelectable =
    (): typeof selectFertilizerEventsTableIsSelectable =>
        selectFertilizerEventsTableIsSelectable;

const selectSelectedIncompleteFertilizerEventsUuids = createDeepEqualSelector(
    selectFertilizerEvents,
    selectActiveTableCellRowIds,
    (fertilizerEvents, uuids: FertilizerEventUuid[]) =>
        uuids.filter(
            (uuid) => !get(fertilizerEvents, [uuid, 'jobs', 0, 'completedAt'])
        )
);

export {
    selectFertilizerEventsTableColumns,
    makeSelectFertilizerEventItems,
    selectFilteredFertilizerEventsArray,
    makeSelectFertilizerEventsTableIsSelectable,
    selectSelectedIncompleteFertilizerEventsUuids
};
